import { useMutation, useQuery } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import {
  MessageBar,
  MessageBarBody,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';
import React from 'react';
import { useParams } from 'react-router-dom';
import {
  CoherencePanelStyles,
  useMessageBarStyles,
} from '../../app/common/styles/CommonStyleObjects';
import {
  GET_COMPANY_CREATION_COMPANY_INFO,
  GET_COMPANY_CREATION_PROGRESS,
  GET_COMPANY_CREATION_REQUEST_DETAILS,
  COMPLETE_SUBLEDGER_INFO,
} from '../../utils/statApi/CompanyCreationApi';

import ActionButtons from '../common/ActionButtons';
import LoadingErrorMessage from '../common/errorContent/LoadingErrorMessage';
import { ICompanyCreationParams } from '../common/ParamTypes';
import CompanyRequestInfoCards from './details/CompanyRequestInfoCards';

interface ICompleteSubledgerReviewPanel {
  closePanel: () => void;
}
const CompleteSubledgerReviewPanel = (props: ICompleteSubledgerReviewPanel): JSX.Element => {
  const { closePanel } = props;
  const { id } = useParams<ICompanyCreationParams>();
  const classes = useMessageBarStyles();
  const companyCreationId = parseInt(id, 10);

  const { data, error, loading } = useQuery(GET_COMPANY_CREATION_COMPANY_INFO, {
    variables: {
      companyCreationId,
    },
  });

  const [sendSubledgerEmail, { loading: emailSending, error: emailError }] = useMutation(
    COMPLETE_SUBLEDGER_INFO,
    {
      onCompleted: closePanel,
      refetchQueries: [
        {
          query: GET_COMPANY_CREATION_PROGRESS,
          variables: {
            companyCreationId,
          },
        },
        {
          query: GET_COMPANY_CREATION_REQUEST_DETAILS,
          variables: {
            companyCreationId,
          },
        },
      ],
    },
  );

  const handleOnSaveClick = (): void => {
    sendSubledgerEmail({ variables: { input: companyCreationId } });
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.large}
      titleText="Complete Subledger Info"
      isOpen
      onDismiss={closePanel}
      hasCloseButton
      styles={CoherencePanelStyles}
      closeButtonAriaLabel="Close"
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          mutationLoading={emailSending}
          closePanel={closePanel}
          handleSubmit={handleOnSaveClick}
          saveLabel="Complete Subledger Info"
          saveTitle="Complete Subledger Info"
          cancelLabel="Cancel Complete Subledger Info"
          cancelTitle="Cancel Complete Subledger Info"
        />
      )}
    >
      <LoadingErrorMessage error={error} loading={loading} />
      <LoadingErrorMessage error={emailError} loading={emailSending} />
      <FluentProvider theme={webLightTheme}>
        <MessageBar layout="multiline" className={classes.messageBar}>
          <MessageBarBody>
            Please review the following information before completing the subledger info.
          </MessageBarBody>
        </MessageBar>
      </FluentProvider>
      <br />
      <CompanyRequestInfoCards
        companyRequestInfo={data?.companyCreationCompanyInfo}
        loading={loading}
        error={error}
        showCurrencyDocument={false}
        showMsSalesLocation={false}
        showShadowCompanyCard={false}
        showStatContactsCard={false}
        showCompanyCreationContactsCard={false}
        showIhccSetup={false}
        showSapSetup={false}
        showFunctionalCurrency={false}
        showSubledger
      />
    </CoherencePanel>
  );
};
export default CompleteSubledgerReviewPanel;
